import { useEffect, useState } from 'react';
import { isValidWifiName, normalizeWifiName } from '../lib/wifiName';

export interface WifiKeyOwner {
    id: number;
    name: string;
    url: string;
}

export interface WifiKey {
    id: number;
    rx_key: string;
    tx_key: string | null;
    comment: string | null;
    created_at: string;
    owner: WifiKeyOwner | null;
}

export type KeysByWifiStatus = 'idle' | 'invalid' | 'loading' | 'ready' | 'error';

export interface KeysByWifiState {
    wifi: string;
    status: KeysByWifiStatus;
    items: WifiKey[];
    truncated: boolean;
}

const DEBOUNCE_MS = 250;
const EMPTY: WifiKey[] = [];

/** Fetches license-8 keys for an exact Wi-Fi name; only fires for valid input. */
export function useKeysByWifi(raw: string): KeysByWifiState {
    const wifi = normalizeWifiName(raw);
    const valid = isValidWifiName(wifi);
    const [fetched, setFetched] = useState<KeysByWifiState | null>(null);

    useEffect(() => {
        if (!valid) return;
        const controller = new AbortController();
        const timer = window.setTimeout(async () => {
            const url = new URL(route('cmdk.keys_by_wifi'), window.location.origin);
            url.searchParams.set('wifi', wifi);
            try {
                const resp = await fetch(url.toString(), {
                    signal: controller.signal,
                    credentials: 'same-origin',
                    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                });
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const json = (await resp.json()) as { items: WifiKey[]; truncated: boolean };
                setFetched({ wifi, status: 'ready', items: json.items ?? EMPTY, truncated: !!json.truncated });
            } catch (e) {
                if ((e as { name?: string }).name === 'AbortError') return;
                setFetched({ wifi, status: 'error', items: EMPTY, truncated: false });
            }
        }, DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [wifi, valid]);

    if (wifi === '') return { wifi, status: 'idle', items: EMPTY, truncated: false };
    if (!valid) return { wifi, status: 'invalid', items: EMPTY, truncated: false };
    // A result for an older input is stale: show loading until the current one lands.
    if (fetched?.wifi !== wifi) return { wifi, status: 'loading', items: EMPTY, truncated: false };
    return fetched;
}
